import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Alert,
  Divider,
  AppBar,
  Toolbar,
  Tooltip,
  CircularProgress,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  FlashOn as FlashOnIcon,
  CloudUpload as UploadIcon,
  CameraAlt as CameraIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as DotIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import { useAuth } from '../../hooks/AuthProvider';
import {GET_DASHBOARD_WABA_STATUS,WA_BUSINESS_ACCOUNT_DATA,WA_BUSINESS_ACCOUNT_PROFILE,WA_BUSINESS_ACCOUNT_ABOUT,WA_BUSINESS_ACCOUNT_PHOTO,SETTINGS_DASHBOARD_ENDPOINT,TENANT_TIMEZONE_ENDPOINT} from '../../config/urls';
import typography from '../../cssUtils/typography';
import { getTimezoneOptions } from '../../common/iana-timezones';

const TIMEZONE_OPTIONS = getTimezoneOptions();

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#25D366',
      dark: '#20bd5a',
    },
    secondary: {
      main: '#128C7E',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
    error: {
      main: '#ef4444',
    },
    success: {
      main: '#10b981',
    },
    warning: {
      main: '#f59e0b',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});


const SettingsDashboardForm = () => {
  const { getValidToken } = useAuth();
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    contactNumber: '',
    email: '',
    website: '',
    vertical: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pinCode: '',
    country: '',
    about: '',
    description: '',
  });

  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [savedPhotoUrl, setSavedPhotoUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [wabaDetails, setWabaDetails] = useState(null);
  const [settings, setSettings] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [loading, setLoading] = useState({
    profile: true,
    photo: true,
    business: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [timezoneLoading, setTimezoneLoading] = useState(true);
  const [savingTimezone, setSavingTimezone] = useState(false);
  
  // WhatsApp API Status (initially empty, will be fetched)
  const [apiStatus, setApiStatus] = useState({
    connected: false,
    messagingLimit: '',
    accountStatus: '',
    canSendMessage: '',
    mmLiteStatus: '',
    phoneQuality: '',
    verificationStatus: '',
    lastUpdated: '',
    emailVerified: '',
  });

  const businessCategories = [
  { value: 'OTHER', label: '📦 Other' },
  { value: 'AUTO', label: '🚗 Automotive' },
  { value: 'BEAUTY', label: '💄 Beauty & Spa' },
  { value: 'APPAREL', label: '👔 Clothing & Apparel' },
  { value: 'EDU', label: '📚 Education' },
  { value: 'ENTERTAIN', label: '🎬 Entertainment' },
  { value: 'EVENT_PLAN', label: '📅 Event Planning' },
  { value: 'FINANCE', label: '💰 Finance & Banking' },
  { value: 'GROCERY', label: '🛒 Grocery' },
  { value: 'GOVT', label: '🏛️ Government' },
  { value: 'HOTEL', label: '🏨 Hotel' },
  { value: 'HEALTH', label: '⚕️ Healthcare' },
  { value: 'NONPROFIT', label: '🤝 Non-Profit' },
  { value: 'PROF_SERVICES', label: '💼 Professional Services' },
  { value: 'RETAIL', label: '🛍️ Retail' },
  { value: 'TRAVEL', label: '✈️ Travel & Hospitality' },
  { value: 'RESTAURANT', label: '🍽️ Restaurant' },
  { value: 'ALCOHOL', label: '🍷 Alcohol' },
  { value: 'ONLINE_GAMBLING', label: '🎰 Online Gambling' },
  { value: 'PHYSICAL_GAMBLING', label: '🎲 Physical Gambling' },
  { value: 'OTC_DRUGS', label: '💊 OTC Drugs' },
  { value: 'MATRIMONY_SERVICE', label: '💍 Matrimony Service' },
];

  const countries = [
    { value: 'US', label: '🇺🇸 United States' },
    { value: 'GB', label: '🇬🇧 United Kingdom' },
    { value: 'CA', label: '🇨🇦 Canada' },
    { value: 'AU', label: '🇦🇺 Australia' },
    { value: 'IN', label: '🇮🇳 India' },
    { value: 'DE', label: '🇩🇪 Germany' },
    { value: 'FR', label: '🇫🇷 France' },
    { value: 'BR', label: '🇧🇷 Brazil' },
    { value: 'MX', label: '🇲🇽 Mexico' },
    { value: 'JP', label: '🇯🇵 Japan' },
  ];

  const tenant = localStorage.getItem('tenant');

  const fetchTenantTimezone = async () => {
    try {
      setTimezoneLoading(true);
      const token = await getValidToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': tenant,
      };
      const response = await axios.get(TENANT_TIMEZONE_ENDPOINT, { headers });
      const zone = response?.data?.timezone;
      const match = TIMEZONE_OPTIONS.find((opt) => opt.value === zone);
      setSelectedTimezone(match || (zone ? { label: zone, value: zone } : null));
      // Keep dateUtils.getTenantTimezone() in sync with the tenant's actual
      // saved value — it otherwise only updates on next login/token refresh.
      if (zone) {
        localStorage.setItem('tenantTimezone', zone);
      }
    } catch (error) {
      console.error('Error fetching tenant timezone:', error);
    } finally {
      setTimezoneLoading(false);
    }
  };

  const handleSaveTimezone = async (option) => {
    if (!option) return;
    try {
      setSavingTimezone(true);
      const token = await getValidToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': tenant,
      };
      await axios.patch(TENANT_TIMEZONE_ENDPOINT, { timezone: option.value }, { headers });
      setSelectedTimezone(option);
      localStorage.setItem('tenantTimezone', option.value);
      showSnackbar('Timezone updated successfully', 'success');
    } catch (error) {
      console.error('Error saving tenant timezone:', error);
      const errorMessage = error?.response?.data?.timezone?.[0] || 'Failed to update timezone';
      showSnackbar(errorMessage, 'error');
    } finally {
      setSavingTimezone(false);
    }
  };

  const fetchSettingsData = async () => {
    try {
      const token = await getValidToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': tenant,
      }
      const response = await axios.get(SETTINGS_DASHBOARD_ENDPOINT, { headers });
      const data = response?.data;
      console.log("Settings Response:", data);
      
      if (data) {
        setSettings(data || null);
      }
    } catch (error) {
      console.error('Error fetching about:', error);
      // Don't show error for empty about
      if (error?.response?.status !== 404) {
        showSnackbar('Error loading business description', 'error');
      }
    }
  };


  // Fetch business data from API
  const fetchBusinessData = async () => {
    try {
      setLoading(prev => ({ ...prev, business: true }));
      
      const headers = {
        'X-Tenant-ID': tenant,
      }
      const response = await axios.get(WA_BUSINESS_ACCOUNT_DATA, { headers });
      const data = response?.data;
      console.log("Business Datas Response:", data);
      
      if (data?.status === 'success' && data?.business) {
        const business = data.business;
        setCharCount(business?.about?.length || 0);
        const combinedAddress = business?.displayAddress;
        setFormData(prev => ({
          ...prev,
          businessName: business?.name || '',
          contactName: business?.contactName || '',
          contactNumber: business?.contactNumber || '',
          email: business?.email || '',
          country: business?.country || '',
          website: business?.website || '',
          about: business?.about || '',
          city: business?.city || '',
          state: business?.state || '',
          pinCode: business?.pinCode || '',
          addressLine1: business?.addressLine1 || '',
          addressLine2: business?.addressLine2 || '',
          vertical: business?.vertical || '',
        }));
        
        // Update API status based on business data
        setApiStatus(prev => ({
          ...prev,
          accountStatus: business?.emailVerified ? 'true' : 'Pending',
          emailVerified: business?.emailVerified ? 'true' : 'Pending', 
          // verificationStatus: wabaDetails?.verificationStatus ? 'true' : 'Pending',
          lastUpdated: new Date(business?.modifiedOn)?.toLocaleString() || 'Unknown',
        }));
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
      setError('Failed to fetch business information');
      showSnackbar('Error loading business data', 'error');
    } finally {
      setLoading(prev => ({ ...prev, business: false }));
    }
  };

  // Fetch profile photo
  const fetchProfilePhoto = async () => {
    try {
      setLoading(prev => ({ ...prev, photo: true }));
      const headers = {
        'X-Tenant-ID': tenant,
      }
      
      const response = await axios.get(WA_BUSINESS_ACCOUNT_PHOTO, { headers });
      const data = response?.data;
      
      if (data?.status === 'success' && data?.message) {
        setImagePreview(data.message);
        setSavedPhotoUrl(data.message);
      }
    } catch (error) {
      console.error('Error fetching profile photo:', error);
      // Don't show error for empty photo
      if (error?.response?.status !== 404) {
        showSnackbar('Error loading profile photo', 'error');
      }
    } finally {
      setLoading(prev => ({ ...prev, photo: false }));
    }
  };

  // Fetch profile about
  const fetchProfileAbout = async () => {
    try {
      setLoading(prev => ({ ...prev, about: true }));
      const headers = {
        'X-Tenant-ID': tenant,
      }
      const response = await axios.get(WA_BUSINESS_ACCOUNT_ABOUT, { headers });
      const data = response?.data;
      
      if (data?.status === 'success' && data?.about) {
        // setFormData(prev => ({ ...prev, about: data?.about?.message || '' }));
        // setCharCount(data?.about?.message?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching about:', error);
      // Don't show error for empty about
      if (error?.response?.status !== 404) {
        showSnackbar('Error loading business description', 'error');
      }
    } finally {
      setLoading(prev => ({ ...prev, about: false }));
    }
  };

  // Fetch complete profile (source of truth for vertical/description, since
  // these are what actually get written back via the profile PUT)
  const fetchProfile = async () => {
    try {
      setLoading(prev => ({ ...prev, profile: true }));
      const headers = {
        'X-Tenant-ID': tenant,
      }

      const response = await axios.get(WA_BUSINESS_ACCOUNT_PROFILE, { headers });
      const data = response?.data;

      if (data?.status === 'success' && data?.profile) {
        const profile = data.profile;
        setFormData(prev => ({
          ...prev,
          vertical: profile?.vertical || prev.vertical,
          description: profile?.desc || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showSnackbar('Error loading profile details', 'error');
    } finally {
      setLoading(prev => ({ ...prev, profile: false }));
    }
  };

  const fetchWabaDetails = async () => {
    try {
      setLoading(prev => ({ ...prev, profile: true }));
      const headers = {
        'X-Tenant-ID': tenant,
      }
      
      const response = await axios.get(GET_DASHBOARD_WABA_STATUS, { headers });
      const data = response?.data;
      console.log("WABA Details Response:", data);
      
      if (data?.status === 'success' ) {
        setWabaDetails(data?.wabaInfo);
        console.log("WABA Details Set:", data?.wabaInfo);
        // Update form with profile data if availabl
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showSnackbar('Error loading profile details', 'error');
    } finally {
      setLoading(prev => ({ ...prev, profile: false }));
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchSettingsData();
    fetchBusinessData();
    fetchProfilePhoto();
    fetchProfile();
    fetchWabaDetails();
    fetchTenantTimezone();
  }, []);

  console.log("Settings Set:", settings);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'about') {
      setCharCount(value?.length || 0);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('File size must be less than 5MB', 'error');
        return;
      }

      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = null;
  };

  const handleCancelPhoto = () => {
    setProfileImage(null);
    setImagePreview(savedPhotoUrl);
  };

  const handleUploadPhoto = async () => {
    if (!profileImage) return;

    try {
      setUploadingPhoto(true);

      const formDataImage = new FormData();
      formDataImage.append('photo', profileImage);

      const response = await axios.put(WA_BUSINESS_ACCOUNT_PHOTO, formDataImage, {
        headers: {
          'X-Tenant-ID': tenant,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.data?.status === 'success') {
        setProfileImage(null);
        await fetchProfilePhoto();
        showSnackbar('Profile picture updated successfully', 'success');
      } else {
        showSnackbar(response?.data?.message || 'Failed to update profile picture', 'error');
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      showSnackbar(error?.response?.data?.message || 'Failed to update profile picture', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
  try {
    setSaving(true);
    setError(null);

    // Prepare data for each API endpoint.
    // Only fields the Gupshup profile PUT actually supports go here — business
    // name/contact/email are read-only (not writable via any Gupshup endpoint).
    const profileData = {
      preferredCountry: formData.country || null,
      addressLine1: formData.addressLine1 || null,
      addressLine2: formData.addressLine2 || null,
      city: formData.city || null,
      state: formData.state || null,
      pinCode: formData.pinCode || null,
      vertical: formData.vertical || null,
      website: formData.website || null,
      description: formData.description || null,
    };

    const aboutData = {
      about: formData.about || null,
    };

    // Helper function to remove null values
    const removeNullValues = (obj) => {
      return Object.fromEntries(
        Object.entries(obj).filter(([_, v]) => v !== null)
      );
    };

    // Clean data
    const cleanProfileData = removeNullValues(profileData);
    const cleanAboutData = removeNullValues(aboutData);

    // Prepare headers
    const headers = {
      'X-Tenant-ID': tenant,
      'Content-Type': 'application/json',
    };

    // Track results
    const results = {};

    // 1. Update Profile (includes business info, address, etc.)
    if (Object.keys(cleanProfileData).length > 0) {
      try {
        results.profile = await axios.put(
          WA_BUSINESS_ACCOUNT_PROFILE,
          cleanProfileData,
          { headers }
        );
      } catch (error) {
        results.profile = { error: error?.response?.data || error.message };
      }
    } else {
      results.profile = { status: 'skipped', message: 'No profile data to update' };
    }

    // 2. Update About (if any data)
    if (Object.keys(cleanAboutData).length > 0) {
      try {
        results.about = await axios.put(
          WA_BUSINESS_ACCOUNT_ABOUT,
          cleanAboutData,
          { headers }
        );
      } catch (error) {
        results.about = { error: error?.response?.data || error.message };
      }
    } else {
      results.about = { status: 'skipped', message: 'No about text to update' };
    }

    // 3. Update Photo (if changed)
    if (profileImage) {
      try {
        const formDataImage = new FormData();
        formDataImage.append('photo', profileImage);
        
        results.photo = await axios.put(
          WA_BUSINESS_ACCOUNT_PHOTO,
          formDataImage,
          {
            headers: {
              'X-Tenant-ID': tenant,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } catch (error) {
        results.photo = { error: error?.response?.data || error.message };
      }
    } else {
      results.photo = { status: 'skipped', message: 'No photo to update' };
    }

    // Check results
    const successfulUpdates = [];
    const failedUpdates = [];

    Object.entries(results).forEach(([api, result]) => {
      if (result.error) {
        failedUpdates.push(`${api}: ${result.error?.message || 'Unknown error'}`);
      } else if (result?.data?.status === 'success' || result.status === 'skipped') {
        successfulUpdates.push(api);
      } else if (result?.data?.status === 'error') {
        failedUpdates.push(`${api}: ${result?.data?.message}`);
      }
    });

    if (failedUpdates.length === 0) {
      showSnackbar('Profile saved successfully!', 'success');
      
      // Refresh data
      await Promise.all([
        fetchSettingsData(),
        fetchBusinessData(),
        fetchProfilePhoto(),
        fetchProfile(),
        fetchWabaDetails(),
      ]);
    } else if (successfulUpdates.length > 0 && failedUpdates.length > 0) {
      // Partial success
      showSnackbar(
        `Partially saved. Successful: ${successfulUpdates.join(', ')}. Failed: ${failedUpdates.join(', ')}`,
        'warning'
      );

      // Still refresh data for successful parts
      await Promise.all([
        fetchBusinessData(),
        fetchProfilePhoto(),
        fetchProfile(),
        fetchWabaDetails(),
      ]);
    } else {
      // All failed
      showSnackbar(
        `Failed to save: ${failedUpdates.join(', ')}`,
        'error'
      );
    }

  } catch (error) {
    console.error('Error saving profile:', error);
    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save profile';
    setError(errorMessage);
    showSnackbar(errorMessage, 'error');
  } finally {
    setSaving(false);
  }
};

  const handleReset = async () => {
    if (window.confirm('🔄 Are you sure you want to reset all changes?')) {
      try {
        // Reset form and reload data from API
        await Promise.all([
          fetchBusinessData(),
          fetchProfilePhoto(),
          fetchProfile(),
        ]);
        showSnackbar('Changes reset successfully', 'info');
      } catch (error) {
        console.error('Error resetting data:', error);
        showSnackbar('Error resetting data', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const StatusCard = ({ icon, label, value, color = 'default' }) => (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
            }}
          >
            {icon}
          </Box>
          <Box flex={1}>
            <Typography
              variant="caption"
              sx={{
                ...typography.bodySmall,
                textTransform: 'uppercase',
                color: 'text.secondary',
                letterSpacing: 0.5,
              }}
            >
              {label}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {color === 'success' && (
                <DotIcon sx={{ fontSize: 8, color: '#10b981' }} />
              )}
              {color === 'warning' && (
                <DotIcon sx={{ fontSize: 8, color: '#f59e0b' }} />
              )}
              <Typography
                variant="body1"
                sx={{
                  ...typography.h2  ,
                  fontWeight: 700,
                  color: color === 'success' ? '#059669' : color === 'warning' ? '#d97706' : 'text.primary',
                }}
              >
                {value || 'Loading...'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const isLoading = Object.values(loading).some(value => value);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 4 }}>

        <Box sx={{ pt: 3, mx: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Business Information with Profile Picture */}
              <Paper sx={{ p: 4, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} pb={2} borderBottom={1} borderColor="divider">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BusinessIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ ...typography.h1}}>
                      Business Information
                    </Typography>
                  </Box>
                  <Chip
                    label={wabaDetails?.verificationStatus === 'verified' ? '✓ Verified' : 'Pending'}
                    size="small"
                    sx={{
                      bgcolor: wabaDetails?.verificationStatus === 'verified' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(251, 191, 36, 0.1)',
                      color: wabaDetails?.verificationStatus === 'verified' ? '#059669' : '#d97706',
                      fontWeight: 700,
                    }}
                  />
                </Box>

                {/* Profile Picture Section */}
                <Box
                  sx={{
                    bgcolor: '#f9fafb',
                    borderRadius: 2,
                    p: 2.5,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <Box position="relative">
                    <Avatar
                      src={imagePreview}
                      sx={{
                        width: 80,
                        height: 80,
                        border: 2,
                        borderColor: 'primary.main',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                      onClick={() => document.getElementById('profile-upload')?.click()}
                    >
                      {formData.businessName?.charAt(0) || '🏢'}
                    </Avatar>
                    <input
                      type="file"
                      id="profile-upload"
                      accept="image/*"
                      hidden
                      onChange={handleImageUpload}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                      }}
                      onClick={() => document.getElementById('profile-upload')?.click()}
                      disabled={uploadingPhoto}
                    >
                      <CameraIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Profile Picture
                    </Typography>
                    {profileImage ? (
                      <Box display="flex" gap={1} mb={1}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={uploadingPhoto ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}
                          onClick={handleUploadPhoto}
                          disabled={uploadingPhoto}
                        >
                          {uploadingPhoto ? 'Saving...' : 'Save Photo'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CloseIcon />}
                          onClick={handleCancelPhoto}
                          disabled={uploadingPhoto}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Click the photo to upload a new one • Square image • Max 5MB
                      </Typography>
                    )}

                    <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Business WA Number
                    </Typography>
                    <Typography variant="headline6" fontWeight={600}>
                      {settings?.business_wa_number}
                    </Typography>
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {/* Form Fields */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Business Name"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      disabled
                      InputProps={{
                        endAdornment: wabaDetails?.verificationStatus === 'verified' && (
                          <Chip
                            label="✓ Verified"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(16, 185, 129, 0.1)',
                              color: '#059669',
                              fontWeight: 700,
                              fontSize: '0.65rem',
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled
                      InputProps={{
                        endAdornment: apiStatus.emailVerified === 'true' && (
                          <Chip
                            label="✓ Verified"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(16, 185, 129, 0.1)',
                              color: '#059669',
                              fontWeight: 700,
                              fontSize: '0.65rem',
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website URL"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth >
                      <InputLabel>Business Category</InputLabel>
                      <Select
                        name="vertical"
                        value={formData.vertical}
                        onChange={handleInputChange}
                        label="Business Category"
                      >
                        {/* First, check if the current value exists in our list */}
                        {!businessCategories.some(cat => cat.value === formData.vertical) && formData.vertical && (
                          <MenuItem value={formData.vertical}>
                            {formData.vertical} {/* Show the raw value from API */}
                          </MenuItem>
                        )}
                        
                        {/* Then render all the predefined categories */}
                        {businessCategories.map((cat) => (
                          <MenuItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Choose the category that best describes your business
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              {/* Regional Settings */}
              <Paper sx={{ p: 4, mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3} pb={2} borderBottom={1} borderColor="divider">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PublicIcon />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ ...typography.h1}}>
                    Regional Settings
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={TIMEZONE_OPTIONS}
                      groupBy={(option) => option.region}
                      value={selectedTimezone}
                      loading={timezoneLoading}
                      disabled
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(_, newVal) => handleSaveTimezone(newVal)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Timezone"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {(timezoneLoading || savingTimezone) ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Drives scheduling, cron jobs, and reporting.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              {/* Business Address */}
              <Paper sx={{ p: 4, mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3} pb={2} borderBottom={1} borderColor="divider">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationIcon />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ ...typography.h1}}>
                    Business Address
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleInputChange}
                      placeholder="Suite, floor, building (optional)"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State / Province"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {/* <FormControl fullWidth >
                      <InputLabel>Country</InputLabel>
                      <Select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        label="Country"
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.value} value={country.value}>
                            {country.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl> */}
                    <TextField
                      fullWidth
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* About Your Business */}
              <Paper sx={{ p: 4, mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3} pb={2} borderBottom={1} borderColor="divider">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionIcon />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ ...typography.h1}}>
                    About Your Business
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="About"
                  name="about"
                  value={formData.about}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 139 }}
                  placeholder="Short status shown in your WhatsApp profile (e.g. 'Hey there! I am using WhatsApp.')"
                />
                <Typography variant="caption" color="text.secondary" textAlign="right" display="block" mt={1} mb={3}>
                  {charCount} / 139 characters
                </Typography>

                <TextField
                  fullWidth
                  label="Business Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={5}
                  inputProps={{ maxLength: 512 }}
                  placeholder="Tell customers what makes your business special. Share your story, products, services, and what sets you apart..."
                />
                <Typography variant="caption" color="text.secondary" textAlign="right" display="block" mt={1}>
                  {formData.description?.length || 0} / 512 characters
                </Typography>
              </Paper>

              {/* WhatsApp API Status */}
              <Paper sx={{ p: 4, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} pb={2} borderBottom={1} borderColor="divider">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FlashOnIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ ...typography.h1}} >
                      WhatsApp API Status
                    </Typography>
                  </Box>
                  <Chip
                    label="🔒 View Only"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(251, 191, 36, 0.1)',
                      color: '#d97706',
                      fontWeight: 700,
                    }}
                  />
                </Box>

                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard 
                      icon="🔗" 
                      label="Status" 
                      value={wabaDetails?.dockerStatus}
                      color={wabaDetails?.dockerStatus === "CONNECTED" ? "success" : "default"} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard icon="📊" label="Messaging Limit" value={wabaDetails?.messagingLimit || "Unknown"} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard 
                      icon="✅" 
                      label="Account Status" 
                      value={wabaDetails?.accountStatus || "Loading..."} 
                      color={wabaDetails?.accountStatus === "ACTIVE" ? "success" : "warning"} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard 
                      icon="💬" 
                      label="Can Send Message" 
                      value={wabaDetails?.canSendMessage || "Checking..."} 
                      color={wabaDetails?.canSendMessage === "LIMITED" ? "warning" : "success"} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard icon="🚀" label="mmLiteStatus" value={wabaDetails?.mmLiteStatus || "Unknown"} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard 
                      icon="📱" 
                      label="Phone Quality" 
                      value={wabaDetails?.phoneQuality || "Checking"} 
                      color={wabaDetails?.phoneQuality === "GREEN" ? "success" : "warning"} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard 
                      icon="✓" 
                      label="Verification Status" 
                      value={wabaDetails?.verificationStatus.toUpperCase()  || "PENDING"} 
                      color={wabaDetails?.verificationStatus === "verified" ? "success" : "warning"} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatusCard icon="🕐" label="Last Updated" value={wabaDetails?.lastUpdated || "Just now"} />
                  </Grid>
                </Grid>

                <Alert
                  icon={<InfoIcon />}
                  severity="info"
                  sx={{
                    bgcolor: 'rgba(37, 211, 102, 0.05)',
                    border: 1,
                    borderColor: 'rgba(37, 211, 102, 0.2)',
                  }}
                >
                  <Typography variant="body2">
                    <strong>About WhatsApp API Status:</strong> These values are automatically synced from your WhatsApp
                    Business API account. Contact support if you notice any discrepancies.
                  </Typography>
                </Alert>
              </Paper>

              {/* Action Bar */}
              <Paper
                sx={{
                  p: 3,
                  position: 'sticky',
                  bottom: 20,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <Box display="flex" gap={2}>
                  <Button variant="outlined" onClick={handleReset} disabled={saving}>
                    ↺ Reset Changes
                  </Button>
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={24} /> : '💾 Save Profile'}
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default SettingsDashboardForm;